import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter } from 'lucide-react';
import InfoHint from './InfoHint';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { fetchEmployeeEfficiency } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanel } from '../contexts/PanelSettingsContext';
import useLatestRequest from '../hooks/useLatestRequest';
import { readEfficiencyRows } from '../utils/employeeEfficiency';
import NodeBarChart from './nodes/NodeBarChart';
import { applyValueShaping, PLOT_AREA_CLASS } from './nodes/nodeChartTheme';

/**
 * Распределение звонков по операторам — «куда звонят клиенты».
 *
 * Живёт отдельно от графиков по нодам сознательно: оператор — это метаданные звонка,
 * а не выход узла анализа, поэтому в каталог пакета такая карточка не попадает и
 * появиться там не может.
 *
 * Источник — серверный агрегат `/analytics/employees/efficiency`, а не выборка звонков
 * с подсчётом на клиенте, как было в старом виджете: тот считал только по загруженной
 * странице журнала и на большом периоде занижал числа.
 */
const TOP_EMPLOYEES = 10;

const CallsByEmployeeChart = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const requests = useLatestRequest();
  const panel = usePanel(PANEL_IDS['ui:dashboard.calls-by-employee']);

  // Направление из верхней панели: раньше карточка его не получала, и «Входящие» меняли
  // все графики раздела, кроме этого.
  const params = useMemo(
    () => ({
      date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      call_type: callType && callType !== 'all' ? callType : undefined
    }),
    [dateRange, callType]
  );

  const load = useCallback(
    async (showSpinner = true) => {
      if (!params.date_from || !params.date_to) {
        return;
      }

      const request = requests.begin({ background: showSpinner === false });
      if (!request) {
        return;
      }

      if (showSpinner) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const data = await fetchEmployeeEfficiency(params);
        if (request.isCurrent()) {
          setRows(readEfficiencyRows(data));
        }
      } catch {
        if (request.isCurrent()) {
          setError('Не удалось загрузить распределение по операторам.');
        }
      } finally {
        const isCurrent = request.isCurrent();
        request.end();
        if (showSpinner && isCurrent) {
          setIsLoading(false);
        }
      }
    },
    [params, requests]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const interval = setInterval(() => load(false), GRAPH_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const spec = useMemo(
    // Один показатель по разным операторам — поэтому один цвет, а не палитра.
    () => ({ sort: 'value_desc', top: TOP_EMPLOYEES, otherBucket: true, tone: 'sequential' }),
    []
  );

  const chartRows = useMemo(() => {
    const total = rows.reduce((sum, row) => sum + (Number(row.calls) || 0), 0);

    const mapped = rows
      .map((row) => ({
        code: row.employeeId ?? row.employeeName ?? '—',
        label: row.employeeName || 'Без оператора',
        calls: Number(row.calls) || 0,
        share: total > 0 ? ((Number(row.calls) || 0) * 100) / total : 0
      }))
      .filter((row) => row.calls > 0);

    return applyValueShaping(mapped, spec);
  }, [rows, spec]);

  const height = Math.min(460, Math.max(200, chartRows.length * 34 + 48));

  const handleSelect = (entry) => {
    if (!onOpenCallJournal || !entry?.code || entry.code === '__other') {
      return;
    }

    onOpenCallJournal({ employee_id: entry.code });
  };

  return (
    <Card data-panel-id={PANEL_IDS['ui:dashboard.calls-by-employee']}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 max-sm:gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <CardTitle>{panel.title}</CardTitle>
            <InfoHint text={panel.description} label="О графике" trackId="dashboard.calls_by_employee.hint" />
          </div>
        </div>
        <button
          type="button"
          data-ev="dashboard.calls_by_employee.reload"
          onClick={() => load()}
          disabled={isLoading}
          aria-label="Обновить"
          className="inline-flex h-8 shrink-0 items-center rounded-md border px-2.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50 max-sm:w-8 max-sm:justify-center max-sm:px-0"
        >
          <Filter className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="max-sm:hidden">{isLoading ? 'Обновление…' : 'Обновить'}</span>
        </button>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}

        {chartRows.length === 0 && !isLoading && !error && (
          <p className="text-sm text-muted-foreground">Нет данных за выбранный период.</p>
        )}

        {chartRows.length > 0 && (
          <div className={PLOT_AREA_CLASS} style={{ minHeight: height }}>
            <NodeBarChart spec={spec} rows={chartRows} height={height} onSelect={handleSelect} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CallsByEmployeeChart;
